/* R148e · Feb 2026 — Lightweight project-tasks quick-view popup.
 * Rendered from Clients → Projects when the user clicks the Tasks
 * action button on a row. Shows a compact list of the project's tasks
 * with title + status + assignee + due date. Click a row to jump to
 * the project detail (Tasks tab). Read-only. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, ListChecks } from "lucide-react";

const STATUS_TONE = {
  done:        "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
  in_progress: "bg-sky-500/10 text-sky-700 border-sky-500/40",
  review:      "bg-amber-500/10 text-amber-700 border-amber-500/40",
  blocked:     "bg-red-500/10 text-red-700 border-red-500/40",
  cancelled:   "bg-gray-500/10 text-gray-500 border-gray-500/40",
  open:        "bg-muted text-muted-foreground border-border",
};

export default function ProjectTasksPopup({ project, wsId, onClose }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [status, setStatus] = useState("all");    // R148f · filter

  useEffect(() => {
    if (!project?.id) return;
    setBusy(true);
    api.get(`/workspaces/${wsId}/tasks-with-risk`, { params: { project_id: project.id, lean: "1", limit: 500 } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
  }, [project?.id, wsId]);

  const filtered = rows.filter((t) => {
    if (status === "all") return true;
    if (status === "open") return t.status !== "done" && t.status !== "cancelled";
    if (status === "blocked") return t.status === "blocked";
    return t.status === status;
  });

  if (!project) return null;
  const openCount = rows.filter((t) => t.status !== "done" && t.status !== "cancelled").length;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="project-tasks-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary" />
            {project.name} · Tasks
            <span className="ml-2 text-xs text-muted-foreground font-normal">{openCount} open / {rows.length} total</span>
            <Link
              to={`/app/projects/${project.id}?tab=tasks`}
              className="ml-auto text-xs inline-flex items-center gap-1 text-primary hover:underline"
              data-testid="project-tasks-popup-open-full"
              onClick={onClose}
            >
              Open full project <ExternalLink className="w-3 h-3" />
            </Link>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
          {/* R148f · Status filter chips */}
          <div className="flex items-center gap-1 flex-wrap mb-3" data-testid="ptp-filter-bar">
            {[
              { v: "all", label: `All (${rows.length})` },
              { v: "open", label: `Open (${rows.filter((t) => t.status !== "done" && t.status !== "cancelled").length})` },
              { v: "in_progress", label: `In progress (${rows.filter((t) => t.status === "in_progress").length})` },
              { v: "review", label: `Review (${rows.filter((t) => t.status === "review").length})` },
              { v: "blocked", label: `Blocked (${rows.filter((t) => t.status === "blocked").length})` },
              { v: "done", label: `Done (${rows.filter((t) => t.status === "done").length})` },
            ].map((f) => (
              <button
                key={f.v}
                type="button"
                onClick={() => setStatus(f.v)}
                className={`h-7 text-[10px] uppercase tracking-widest px-2 border ${status === f.v ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted/40 border-border"}`}
                data-testid={`ptp-filter-${f.v}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {busy ? (
            <div className="text-sm text-muted-foreground text-center py-8">Loading tasks…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border">
              {rows.length === 0 ? "No tasks in this project yet." : "No tasks match this filter."}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40 border-b border-border">
                  <th className="px-3 py-2">Task</th>
                  <th className="px-3 py-2 w-32">Status</th>
                  <th className="px-3 py-2 w-40">Assignee</th>
                  <th className="px-3 py-2 w-28">Due</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b border-border hover:bg-muted/20" data-testid={`ptp-row-${t.id}`}>
                    <td className="px-3 py-1.5">
                      <Link to={`/app/tasks?open=${t.id}`} className="hover:text-primary" onClick={onClose}>
                        <span className={t.status === "done" ? "line-through text-muted-foreground" : "font-medium"}>{t.title}</span>
                      </Link>
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge className={`rounded-none text-[10px] border ${STATUS_TONE[t.status] || STATUS_TONE.open}`}>
                        {t.status?.replace("_", " ") || "open"}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground truncate">{t.assignee_name || "—"}</td>
                    <td className="px-3 py-1.5 font-mono">{t.due_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
